import React, { useCallback, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Navbar } from './components/Navbar.js';
import { Dashboard } from './components/Dashboard.js';
import { DriverDashboard } from './components/DriverDashboard.js';
import { BookingForm } from './components/BookingForm.js';
import { BookingsTable } from './components/BookingsTable.js';
import { Settings } from './components/Settings.js';
import { PriceList } from './components/PriceList.js';
import { Toaster, toast } from 'react-hot-toast';
import { Login } from './components/Login';

const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const { exp } = JSON.parse(jsonPayload);
    return Date.now() >= exp * 1000;
  } catch (e) {
    return true;
  }
};


const ProtectedRoute = ({ children, allowedRoles }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user')); // { name, user_type }

  if (!token || isTokenExpired(token)) {
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(Number(user.user_type))) {
    return <Navigate to={user.user_type === 1 ? "/dashboard" : "/driverdashboard"} replace />;
  }

  return children;
};
const HomeRedirect = () => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user'));

  if (!token || isTokenExpired(token)) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={user?.user_type === 1 ? "/dashboard" : "/driverdashboard"} replace />;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = location.pathname;
  const isLoginPage = location.pathname === '/login';

  const handleLogout = useCallback((message = "Session expired. Please login again.") => {
    localStorage.clear();
    if (message) toast.error(message);
    navigate('/login');
  }, [navigate]);

  // 2. Enhanced Session Check
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    // If we are not on login page and have no token OR expired token
    if (!isLoginPage) {
      if (!token) {
        navigate('/login');
      } else if (isTokenExpired(token)) {
        handleLogout();
      }
    }
  }, [location, navigate, handleLogout, isLoginPage]);

  const setActiveTab = (path) => {
    navigate(path.startsWith('/') ? path : `/${path}`);
  };

  return (
    <div className="App min-h-screen bg-gray-50 font-sans text-gray-900">
      <Toaster position="top-center" reverseOrder={false} />

      {!isLoginPage && <Navbar activeTab={activeTab} setActiveTab={setActiveTab} />}

      <main className={!isLoginPage ? "max-w-7xl mx-auto px-4 py-8" : ""}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            
            {/* 3. Handle the root path */}
            <Route path="/" element={<HomeRedirect />} />

            <Route path="/login" element={<Login />} />

            {/* ADMIN ONLY ROUTES */}
            <Route path="/dashboard" element={
              <ProtectedRoute allowedRoles={[1]}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                  <Dashboard />
                </motion.div>
              </ProtectedRoute>
            } />

            <Route path="/priceList" element={
              <ProtectedRoute allowedRoles={[1]}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                  <PriceList />
                </motion.div>
              </ProtectedRoute>
            } />

            <Route path="/booking" element={
              <ProtectedRoute allowedRoles={[1]}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                  <BookingForm />
                </motion.div>
              </ProtectedRoute>
            } />

            <Route path="/settings" element={
              <ProtectedRoute allowedRoles={[1]}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                  <Settings />
                </motion.div>
              </ProtectedRoute>
            } />

            <Route path="/history" element={
              <ProtectedRoute allowedRoles={[1, 2]}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900">Booking History</h2>
                    <BookingsTable />
                  </div>
                </motion.div>
              </ProtectedRoute>
            } />

            <Route path="/driverdashboard" element={
              <ProtectedRoute allowedRoles={[1,2]}>
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
                  <DriverDashboard />
                </motion.div>
              </ProtectedRoute>
            } />

            {/* 4. Fallback for unknown routes - Redirect back to root logic */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </AnimatePresence>
      </main>
    </div>
  );
}

export default App;